import { useRef, type KeyboardEvent } from 'react';

interface LikertScaleProps {
  value: number | undefined;
  onChange: (value: number) => void;
  anchors: [string, string];
  name: string;
}

// Escala 0–10 segmentada con anclas en los extremos (handoff §8, §10).
export function LikertScale({ value, onChange, anchors, name }: LikertScaleProps) {
  const refs = useRef<Array<HTMLDivElement | null>>([]);
  const nums = Array.from({ length: 11 }, (_, i) => i);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>, n: number) => {
    let next: number | null = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') next = Math.min(10, n + 1);
    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') next = Math.max(0, n - 1);
    if (next !== null) {
      e.preventDefault();
      onChange(next);
      refs.current[next]?.focus();
    }
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      onChange(n);
    }
  };

  return (
    <div className="gb-likert">
      <div className="gb-likert__grid" role="radiogroup" aria-label={name}>
        {nums.map((n) => {
          const selected = value === n;
          const tabbable = selected || (value == null && n === 0);
          return (
            <div
              key={n}
              ref={(el) => {
                refs.current[n] = el;
              }}
              role="radio"
              aria-checked={selected}
              tabIndex={tabbable ? 0 : -1}
              className={'gb-likert__cell' + (selected ? ' is-selected' : '')}
              onClick={() => onChange(n)}
              onKeyDown={(e) => onKeyDown(e, n)}
              aria-label={String(n)}
            >
              {selected ? (
                <span className="gb-likert__check" aria-hidden="true">
                  ✓
                </span>
              ) : null}
              <span className="gb-likert__num">{n}</span>
            </div>
          );
        })}
      </div>
      <div className="gb-likert__anchors" aria-hidden="true">
        <span>0 · {anchors[0]}</span>
        <span>10 · {anchors[1]}</span>
      </div>
    </div>
  );
}
